import { getInitials } from "@gettactic/helpers";
import {
  getAvatarGradientStops,
  getAvatarTextColor,
  getRandomColor
} from "@gettactic/helpers/src/colors";
import { getCssVariableValue } from "@gettactic/helpers/src/css/getCssVariableValue";
import React from "react";
import { SpotLayout } from "../../types";

export type CircleProfilePhotoSVGProps = {
  profilePhoto: string;
  name: string;
  restricted?: boolean;
  privacy?: boolean;
  layout: SpotLayout;
  radius: number;
  hasCheckedIn?: boolean;
  grayscale?: boolean;
  /** When the spot is selected, draw the ring in the selection color. */
  isSelected?: boolean;
};

/**
 * Occupant avatar drawn at a spot's dot position — visual parity with the
 * legacy overlay's `CircleProfilePhoto`/`InteractiveActiveSpot`: profile
 * photo clipped to a circle, or initials on a name-derived background
 * color. Privacy mode renders an anonymous circle (never the identity).
 */
export const CircleProfilePhotoSVG: React.FC<CircleProfilePhotoSVGProps> = ({
  profilePhoto,
  name,
  restricted = false,
  privacy = false,
  layout,
  radius,
  hasCheckedIn = false,
  grayscale = false,
  isSelected = false
}) => {
  const cx = layout.circle.x;
  const cy = layout.circle.y;
  const displayName = privacy ? "" : name;
  const initials = getInitials(displayName || "");
  // Same background-color derivation as the legacy avatar so the initials
  // fallback looks identical on both map types.
  const bgColor = grayscale
    ? "gray"
    : (initials ? getRandomColor({ string: displayName }) : undefined) ||
      "#223c5b";
  // A selected spot's ring takes the selection color, mirroring the legacy map
  // (`.interactive-zone.selected .circle-profile-photo .wrapper-circle`); a
  // checked-in occupant is otherwise green, everything else white.
  const ringColor = isSelected
    ? getCssVariableValue("--map-spot-selected-stroke") || "#4F48F7"
    : hasCheckedIn
      ? "#34d399"
      : "white";
  const showPhoto = !!profilePhoto && !privacy;
  // SVG ids live in a single document-wide namespace while `cx`/`cy` are
  // spot-local coordinates shared by every spot of the same orientation, so
  // keying ids on them made all avatars resolve to one another's definitions.
  // Key them on their own content instead: duplicates are then identical.
  const clipId = `clip-${cx}-${cy}-${radius}`;
  // Gradient fallback fill, mirroring the HTML avatar's `getAvatarGradient`.
  const gradientStops = getAvatarGradientStops(bgColor);
  const gradientId = `avatar-grad-${bgColor.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <g opacity={restricted ? 0.5 : 1}>
      <title>{displayName}</title>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={gradientStops.light} />
          <stop offset="1" stopColor={gradientStops.dark} />
        </linearGradient>
      </defs>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={`url(#${gradientId})`}
        stroke={ringColor}
        strokeWidth={2}
      />
      {showPhoto ? (
        <>
          <defs>
            <clipPath id={clipId}>
              <circle cx={cx} cy={cy} r={radius} />
            </clipPath>
          </defs>
          <image
            href={profilePhoto}
            x={cx - radius}
            y={cy - radius}
            width={radius * 2}
            height={radius * 2}
            clipPath={`url(#${clipId})`}
            preserveAspectRatio="xMidYMid slice"
            className={grayscale ? "grayscale" : undefined}
          />
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={2}
          />
        </>
      ) : (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: `${radius / 1.3}px`,
            // SVG text defaults to the browser serif at normal weight; the
            // HTML avatar renders its initials `font-inter font-semibold`.
            fontFamily: "var(--font-inter, Inter, sans-serif)",
            fontWeight: 600,
            fill: getAvatarTextColor(bgColor)
          }}
        >
          {initials}
        </text>
      )}
    </g>
  );
};
